import React from 'react';
import {
  ShoppingCart,
  Plus,
  Minus,
  CheckCircle,
  X,
  Loader2,
  Sparkles
} from 'lucide-react';

const PDVAdditionalsModal = ({
  selectedProduct,
  setShowAdditionalsModal,
  setSelectedProduct,
  setSelectedAdditionals,
  setProductQuantity,
  setProductGroupsWithItems,
  loadingAdditionals,
  productGroupsWithItems,
  selectedAdditionals,
  toggleAdditional,
  productQuantity,
  addToCartWithAdditionals,
  canAddToCart,
  calculateProductTotal
}) => {
  return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000001] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header do Modal */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-xl">{selectedProduct.name}</h2>
                <p className="text-orange-100 text-sm">R$ {selectedProduct.price.toFixed(2)}</p>
              </div>
              <button
                onClick={() => {
                  setShowAdditionalsModal(false);
                  setSelectedProduct(null);
                  setSelectedAdditionals({});
                  setProductQuantity(1);
                  setProductGroupsWithItems([]);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                data-testid="close-additionals-modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Corpo do Modal - Grupos de Adicionais */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {loadingAdditionals ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-3" />
                  <p className="text-slate-500 font-medium">Carregando adicionais...</p>
                </div>
              ) : productGroupsWithItems.length > 0 ? (
                productGroupsWithItems.map(group => {
                  const selectedCount = (selectedAdditionals[group.id] || []).length;
                  const minItems = group.min_items || 0;
                  const maxItems = group.max_items || 10;
                  const isRequired = minItems > 0 || group.is_required;

                  return (
                    <div key={group.id} className="bg-slate-50 rounded-2xl p-4" data-testid={`additional-group-${group.id}`}>
                      {/* Header do Grupo */}
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            {group.name}
                            {isRequired && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                                Obrigatório
                              </span>
                            )}
                          </h3>
                          <p className="text-slate-500 text-xs">
                            {minItems > 0 ? `Mínimo ${minItems}` : 'Opcional'}
                            {maxItems < 10 && ` • Máximo ${maxItems}`}
                            {selectedCount > 0 && ` • ${selectedCount} selecionado(s)`}
                          </p>
                        </div>
                      </div>

                      {/* Adicionais do Grupo */}
                      <div className="flex flex-wrap gap-2">
                        {(group.additionals || []).filter(a => a.available !== false).map(additional => {
                          const isSelected = (selectedAdditionals[group.id] || []).some(a => a.id === additional.id);
                          
                          return (
                            <button
                              key={additional.id}
                              onClick={() => toggleAdditional(group.id, additional)}
                              data-testid={`additional-item-${additional.id}`}
                              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                                isSelected
                                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30 scale-105 ring-2 ring-orange-300'
                                  : 'bg-white text-slate-700 hover:bg-orange-50 border-2 border-slate-200 hover:border-orange-300'
                              }`}
                            >
                              {isSelected && (
                                <CheckCircle className="w-4 h-4" />
                              )}
                              <span>{additional.name}</span>
                              {additional.price > 0 && (
                                <span className={`text-xs ${isSelected ? 'text-orange-100' : 'text-slate-400'}`}>
                                  +R$ {additional.price.toFixed(2)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Nenhum adicional disponível</p>
                  <p className="text-sm text-slate-400 mt-1">Este produto não possui adicionais configurados</p>
                </div>
              )}
            </div>

            {/* Footer do Modal */}
            <div className="border-t border-slate-200 p-4 bg-white">
              {/* Quantidade */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-slate-600 font-medium">Quantidade:</span>
                <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-1">
                  <button
                    onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}
                    className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-all shadow-sm"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-12 text-center font-bold text-lg text-slate-800">{productQuantity}</span>
                  <button
                    onClick={() => setProductQuantity(productQuantity + 1)}
                    className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-all shadow-sm"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Botão Adicionar */}
              <button
                onClick={addToCartWithAdditionals}
                disabled={!canAddToCart}
                className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                  canAddToCart
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/30'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Adicionar • R$ {calculateProductTotal.toFixed(2)}</span>
              </button>

              {!canAddToCart && (
                <p className="text-center text-red-500 text-sm mt-2">
                  Selecione os itens obrigatórios
                </p>
              )}
            </div>
          </div>
        </div>
  );
};

export default PDVAdditionalsModal;
